import { useMemo } from "react";
import { buildInsights } from "../utils/buildInsights";

/**
 * Aggregates transaction, budget, and category data for the dashboard.
 *
 * @param {Object} params
 * @param {Array}  params.txns          - All transactions
 * @param {Array}  params.incomeList    - Income category names
 * @param {Object} params.expenseMap    - { groupName: [subCat, ...] }
 * @param {Function} params.getGroup    - (category) => groupName
 * @param {Object} params.currentBudgets - { category: amount }
 * @param {number} params.selMonth
 * @param {number} params.selYear
 */
export function useDashboardData({
  txns,
  incomeList,
  expenseMap,
  getGroup,
  currentBudgets,
  selMonth,
  selYear,
}) {
  return useMemo(() => {
    // Current month transactions
    const monthTxns = txns.filter((t) => {
      if (!t || !t.date) return false;
      const d = new Date(t.date);
      return d.getMonth() === selMonth && d.getFullYear() === selYear;
    });

    const monthIncome = monthTxns
      .filter((t) => incomeList.includes(t.category))
      .reduce((s, t) => s + (Number(t.amount) || 0), 0);

    const monthExpenses = monthTxns
      .filter((t) => !incomeList.includes(t.category))
      .reduce((s, t) => s + (Number(t.amount) || 0), 0);

    const monthNet = monthIncome - monthExpenses;
    const savingsRate = monthIncome > 0 ? (monthNet / monthIncome) * 100 : 0;

    // Expenses by group
    const expByGroup = {};
    Object.keys(expenseMap).forEach((g) => {
      expByGroup[g] = 0;
    });
    monthTxns
      .filter((t) => !incomeList.includes(t.category))
      .forEach((t) => {
        const g = getGroup(t.category);
        expByGroup[g] = (expByGroup[g] || 0) + (Number(t.amount) || 0);
      });

    // Pie chart data
    const pieData = Object.entries(expByGroup)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({
        name: String(name),
        value: Math.round(value * 100) / 100,
      }));

    // Budget vs actual chart data
    const budgetVsActualData = Object.keys(expenseMap)
      .map((g) => ({
        name:
          String(g).length > 11 ? String(g).slice(0, 10) + ".." : String(g),
        Budgeted: (expenseMap[g] || []).reduce(
          (z, c) => z + (Number(currentBudgets[c]) || 0),
          0
        ),
        Actual: Number(expByGroup[g]) || 0,
      }))
      .filter((d) => d.Budgeted > 0 || d.Actual > 0);

    // Previous month for comparison
    const prevMonth = selMonth === 0 ? 11 : selMonth - 1;
    const prevYear = selMonth === 0 ? selYear - 1 : selYear;
    const prevMonthTxns = txns.filter((t) => {
      if (!t || !t.date) return false;
      const d = new Date(t.date);
      return d.getMonth() === prevMonth && d.getFullYear() === prevYear;
    });
    const prevExpenses = prevMonthTxns
      .filter((t) => !incomeList.includes(t.category))
      .reduce((s, t) => s + (Number(t.amount) || 0), 0);

    const prevExpByGroup = {};
    Object.keys(expenseMap).forEach((g) => {
      prevExpByGroup[g] = 0;
    });
    prevMonthTxns
      .filter((t) => !incomeList.includes(t.category))
      .forEach((t) => {
        const g = getGroup(t.category);
        prevExpByGroup[g] =
          (prevExpByGroup[g] || 0) + (Number(t.amount) || 0);
      });

    const insights = buildInsights({
      monthExpenses, monthIncome, savingsRate, pieData,
      expByGroup, currentBudgets, expenseMap,
      prevExpenses, prevExpByGroup, prevMonth,
    });

    return {
      monthTxns,
      monthIncome,
      monthExpenses,
      monthNet,
      savingsRate,
      expByGroup,
      pieData,
      budgetVsActualData,
      insights,
    };
  }, [txns, incomeList, expenseMap, getGroup, currentBudgets, selMonth, selYear]);
}
